import { useState } from 'react';
import { Modal } from '@design-system/components/modal/modal';
import { Col, Row } from '@design-system/components/grid/grid';
import { Input } from '@design-system/components/input/input';
import { Typography } from '@design-system/components/typography/typography';
import { MediaEditor } from '../../../workout/components/MediaEditor';
import { PoseGuide } from '../../model/Challenges.types';
import {
  MAX_POSE_NAME_LENGTH,
  POSE_PHOTO_HEIGHT,
  POSE_PHOTO_WIDTH,
} from '../../pose-guide.constants';

type PoseModalProps = {
  mode: 'add' | 'edit';
  initial: PoseGuide;
  uploadImage: (file: File) => Promise<string>;
  onSave: (pose: PoseGuide) => void;
  onClose: () => void;
};

export const PoseModal = (p: PoseModalProps) => {
  const [name, setName] = useState(p.initial.name);
  const [imageUrl, setImageUrl] = useState(p.initial.imageUrl);
  const [uploading, setUploading] = useState(false);

  const trimmed = name.trim();
  const isValid =
    Boolean(imageUrl) &&
    trimmed.length > 0 &&
    trimmed.length <= MAX_POSE_NAME_LENGTH;

  const upload = (file: File) => {
    setUploading(true);
    p.uploadImage(file)
      .then(setImageUrl)
      .finally(() => setUploading(false));
  };

  return (
    <Modal
      isOpen
      onClose={p.onClose}
      title={
        p.mode === 'add' ? 'Add pose photo guide' : 'Edit pose photo guide'
      }
      primaryCta={{
        caption: p.mode === 'add' ? 'Add' : 'Save',
        onClick: () => p.onSave({ name: trimmed, imageUrl }),
        state: isValid ? undefined : 'disabled',
      }}
      secondaryCta={{ caption: 'Cancel', onClick: p.onClose }}
    >
      <Row gap="xl">
        <MediaEditor
          format="image"
          accept="image/*"
          src={imageUrl || null}
          loading={uploading}
          disabled={false}
          onUpload={upload}
          onRemove={() => setImageUrl('')}
          width={POSE_PHOTO_WIDTH}
          height={POSE_PHOTO_HEIGHT}
        />
        <Col gap="s" style={{ flex: 1 }}>
          <Input
            label="Title"
            required
            placeholder="Pose name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Typography variant="body" size="small">
            Shown to consumers above this photo. Keep it short - 2 to 4 words
            works best (Maximum 24 characters).
          </Typography>
        </Col>
      </Row>
    </Modal>
  );
};
